import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { BackgroundGlow } from "~/components/background-glow";

export const Route = createFileRoute("/privacy")({
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <div className="relative min-h-screen">
      <BackgroundGlow />

      <div className="relative mx-auto max-w-4xl px-6 py-16">
        <div className="mb-10">
          <h1 className="text-3xl font-extrabold tracking-tight text-foreground md:text-4xl">
            Overseer – Privacy Policy
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Last updated: 26 May 2026
          </p>
        </div>

        <div className="mb-10 space-y-6 rounded-2xl border border-border bg-card/30 p-6 shadow-xl shadow-primary/5 backdrop-blur-md backdrop-saturate-150 md:p-8">
            <p className="text-sm leading-relaxed text-muted-foreground">
              This Privacy Policy explains how Overseer (“we”, “us”, “our”)
              collects, uses, and stores personal data when you use our Discord
              bot, website, and related services (the “Service”).
            </p>

            <p className="text-sm leading-relaxed text-muted-foreground">
              Overseer is operated by independent individuals based in the
              United Kingdom.
            </p>

            <p className="text-sm leading-relaxed text-muted-foreground">
              For data protection enquiries, contact:{" "}
              <a
                href="mailto:data.protection@ovsr.net"
                className="font-semibold text-foreground underline-offset-4 hover:underline"
              >
                data.protection@ovsr.net
              </a>
            </p>
          </div>

        <div className="space-y-6">
          <Section title="1. Scope">
            <p>This Privacy Policy applies to:</p>
            <ul className="list-disc pl-6">
              <li>Discord server owners and administrators who install Overseer</li>
              <li>Users of Discord servers that use Overseer</li>
              <li>
                ARK: Survival Evolved (Xbox) players whose gameplay data is
                processed through Nitrado-hosted servers connected to Overseer
              </li>
              <li>Website users and subscription holders</li>
            </ul>
          </Section>

          <Section title="2. Data We Collect">
            <h3 className="font-semibold">A. Discord Data</h3>
            <p>We store:</p>
            <ul className="list-disc pl-6">
              <li>Discord user ID</li>
              <li>Discord username</li>
              <li>Guild (server) ID</li>
              <li>Commands executed</li>
              <li>Command arguments</li>
              <li>Timestamp of command</li>
              <li>Actions performed by the command</li>
            </ul>
            <p>
              Discord command logs are stored indefinitely. We do not store
              general Discord message content outside of ARK-related logs.
            </p>

            <h3 className="mt-6 font-semibold">
              B. ARK Server Data (via Nitrado API)
            </h3>
            <p>
              Using the Nitrado API token provided by server administrators, we collect and process gameplay data from ARK: Survival Evolved (Xbox) servers hosted through Nitrado.
            </p>
            <p>
              This includes:
            </p>
            <ul className="list-disc pl-6">
              <li>Gamertags</li>
              <li>Platform identifiers</li>
              <li>Historical name changes</li>
              <li>Character data</li>
              <li>Tribe membership history</li>
              <li>Cluster join history</li>
              <li>Ban records issued via Overseer</li>
              <li>
                Server event logs including:
                <ul className="list-disc pl-6">
                  <li>Chat messages</li>
                  <li>Player joins and leaves</li>
                  <li>Admin commands</li>
                  <li>Tribe logs</li>
                </ul>
              </li>
            </ul>

            <p>
              Raw server logs are stored for 60 days, aggregated into
              statistical summaries, and then permanently deleted.
            </p>

            <h3 className="mt-6 font-semibold">
              C. Subscription & Payment Data
            </h3>
            <p>Payments are processed by Stripe.</p>
            <p>Within our own database, we store only:</p>
            <ul className="list-disc pl-6">
              <li>Stripe customer ID</li>
              <li>Subscription ID</li>
              <li>Subscription status</li>
              <li>Subscription type</li>
              <li>Subscription period dates</li>
            </ul>
            <p>
              We do not store card numbers, billing addresses, or payment
              method details. Payments are processed by Stripe under their
              own privacy policy.
            </p>
          </Section>

          <Section title="3. Hosting and Storage">

            <ul className="list-disc pl-6">
              <li>Bot and database hosted on a Hostinger VPS</li>
              <li>Backups stored on Google Drive</li>
              <li>Backups retained for 6 months</li>
            </ul>
            <p>
              Backups are not used for active processing.
            </p>
          </Section>

          <Section title="4. How We Use Personal Data">
            <p>We use personal data to:</p>
            <ul className="list-disc pl-6">
              <li>Providing server management tools</li>
              <li>Monitoring ARK server activity</li>
              <li>Generating player statistics</li>
              <li>Enforcing bans and moderation actions</li>
              <li>Providing subscription features</li>
              <li>Improving, testing, and developing Overseer</li>
            </ul>
            <p>
              We reserve the right to use stored data for internal testing
              and development purposes.
            </p>
            <p>We do not sell personal data.</p>
          </Section>

          <Section title="5. Lawful Basis for Processing">
          <p>We process personal data under the following lawful bases:</p>
            <h3 className="font-semibold">Legitimate Interests</h3>
            <ul className="list-disc pl-6">
              <li>Operating moderation tools</li>
              <li>Preventing abuse and ban evasion</li>
              <li>Maintaining service security</li>
              <li>Improving the Service</li>
            </ul>
            <p>We consider these interests proportionate and necessary for the operation of Overseer.</p>

            <h3 className="mt-6 font-semibold">Contract</h3>
            <p>
              Where processing is necessary to provide paid subscription
              services.
            </p>
          </Section>

          <Section title="6. Children’s Data">
            <p>
              ARK: Survival Evolved (Xbox) may include players under 18.
              We do not verify age information. Gameplay data is processed
              as part of standard server logging functionality.
            </p>
          </Section>

          <Section title="7. Data Sharing">
            <p>We share data only with:</p>
            <ul className="list-disc pl-6">
              <li>Hostinger (hosting)</li>
              <li>Google Drive (backups)</li>
              <li>Stripe (payments)</li>
              <li>Discord and Nitrado APIs</li>
            </ul>
            <p>We do not sell or trade personal data.</p>
            <p>Where third-party providers process data outside the UK, they do so under their own compliance mechanisms.</p>
          </Section>

          <Section title="8. Data Retention">
            <ul className="list-disc pl-6">
              <li>Gamertag profile data stored indefinitely</li>
              <li>Server logs stored for 60 days before deletion</li>
              <li>Discord command logs stored indefinitely</li>
              <li>Backups retained for 6 months</li>
            </ul>
          </Section>

          <Section title="9. Data Security">
            <ul className="list-disc pl-6">
              <li>API tokens encrypted before storage</li>
              <li>Restricted infrastructure access</li>
              <li>Hostinger VPS hosting</li>
              <li>Secure backup retention</li>
            </ul>
            <p>
              While reasonable measures are taken, no system is completely
              secure.
            </p>
          </Section>

          <Section title="10. Your Rights">
            <p>Under UK GDPR, individuals have the right to:</p>
            <ul className="list-disc pl-6">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion</li>
              <li>Object to processing</li>
              <li>Lodge a complaint with the UK ICO</li>
            </ul>
            <p>
              Contact:{" "}
              <a
                href="mailto:data.protection@ovsr.net"
                className="font-semibold text-foreground underline-offset-4 hover:underline"
              >
                data.protection@ovsr.net
              </a>
            </p>
          </Section>

          <Section title="11. Deletion Requests">
            <p>
              Individuals may request deletion of their personal data by
              contacting the email address above.
            </p>

            <h3 className="mt-4 font-semibold">What We Will Delete</h3>
            <ul className="list-disc pl-6">
              <li>Gamertag profile data</li>
              <li>Historical name records</li>
              <li>Character data</li>
              <li>Tribe history linked to that gamertag</li>
              <li>Cluster join history</li>
              <li>Aggregated statistics linked to the profile</li>
            </ul>

            <h3 className="mt-4 font-semibold">What We May Retain</h3>
            <p>
              We may retain limited information necessary for enforcing bans,
              preventing abuse or ban evasion, protecting service integrity,
              defending legal claims, or complying with legal obligations.
            </p>

            <h3 className="mt-4 font-semibold">Backups</h3>
            <p>
              Deleted data may remain in disaster recovery backups for up to
              6 months. If restored, deletion requests will be re-applied.
            </p>
          </Section>

          <Section title="12. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time.
              Continued use of the Service constitutes acceptance of the
              updated version.
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-border bg-card/30 p-6 shadow-xl shadow-primary/5 backdrop-blur-md backdrop-saturate-150 md:p-8">
      <h2 className="mb-4 text-lg font-bold tracking-tight text-foreground">
        {title}
      </h2>
      <div className="space-y-3 text-sm leading-relaxed text-muted-foreground [&_a]:font-semibold [&_a]:text-foreground [&_a]:underline-offset-4 [&_a]:hover:underline [&_h3]:font-semibold [&_h3]:text-foreground [&_strong]:font-semibold [&_strong]:text-foreground [&_ul]:space-y-1.5 [&_ul]:pl-6 [&_ul]:marker:text-primary">
        {children}
      </div>
    </section>
  );
}
